import { useCallback, useMemo, useRef, useState } from 'react';
import { MdAttachFile, MdDescription, MdCancel } from 'react-icons/md';
import { Button } from '@/components/Button';
import { ATTACHMENT_ACCEPT, validateAttachments } from '@/utils/chatAttachments';
import useFetchCompanyChatComments from '../api/useFetchCompanyChatComments';
import usePostCompanyChatComment from '../api/usePostCompanyChatComment';
import useDeleteCompanyChatComment from '../api/useDeleteCompanyChatComment';
import CompaniesTimelineItem from './CompaniesTimelineItem';
import CompaniesTimelineDeleteModal from './CompaniesTimelineDeleteModal';
import { showErrorToast, showSuccessToast } from '../../job_change_processes/lib/toast';
import type { AgentApiCompanyChatCommentsRecord } from '@/types/serializers';

const INPUT_MIN_HEIGHT = 75;
const INPUT_MAX_HEIGHT = 134;

// 内容に応じて INPUT_MAX_HEIGHT まで伸ばし、それ以降はスクロールさせる（Figma 仕様）
const resizeInput = (el: HTMLTextAreaElement) => {
  el.style.height = 'auto';
  el.style.height = `${Math.min(Math.max(el.scrollHeight, INPUT_MIN_HEIGHT), INPUT_MAX_HEIGHT)}px`;
  el.style.overflowY = el.scrollHeight > INPUT_MAX_HEIGHT ? 'auto' : 'hidden';
};

type Props = {
  companyId: number;
  currentUserId?: number;
};

export default function CompaniesTimelineCard({ companyId, currentUserId }: Props) {
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AgentApiCompanyChatCommentsRecord | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const { data, isFetching, hasNextPage, isFetchingNextPage, fetchNextPage, refetch } =
    useFetchCompanyChatComments(companyId);
  const { mutateAsync: postChatComment, isPending: isPosting } = usePostCompanyChatComment();
  const { mutateAsync: deleteChatComment, isPending: isDeleting } = useDeleteCompanyChatComment();

  const chatComments = useMemo(() => data?.pages.flatMap((page) => page.records) ?? [], [data]);

  const loaderRef = useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      if (!node) return;

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        { threshold: 0.5 }
      );
      observerRef.current.observe(node);
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  const handleFilesSelected = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;

    const { accepted, error } = validateAttachments(attachments, Array.from(fileList));
    setAttachments((prev) => [...prev, ...accepted]);
    setAttachmentError(error);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
    setAttachmentError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const content = body.trim();
    if (!content) return;

    try {
      await postChatComment({ companyId, body: content, attachments });
      setBody('');
      setAttachments([]);
      setAttachmentError(null);
      if (textareaRef.current) resizeInput(textareaRef.current);
      await refetch();
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : '送信に失敗しました');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      await deleteChatComment({ companyId, chatCommentId: deleteTarget.id });
      setDeleteTarget(null);
      showSuccessToast('メッセージを削除しました');
      await refetch();
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : '削除に失敗しました');
    }
  };

  return (
    <div className="flex flex-col gap-md p-lg">
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-xs rounded-sm bg-white p-md shadow-layer1"
      >
        <div className="flex flex-col gap-xs rounded-sm border border-solid border-border bg-white p-sm focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-primary">
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-xxs">
              {attachments.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className="flex h-9 max-w-60 items-center gap-xxs rounded-sm border border-solid border-border bg-gray-50 px-xs text-body-xs text-main"
                >
                  <MdDescription className="size-4 shrink-0" />
                  <span className="min-w-0 truncate">{file.name}</span>
                  <button
                    type="button"
                    aria-label="添付を削除"
                    className="flex cursor-pointer border-0 bg-transparent p-0 text-main hover:text-placeholder"
                    onClick={() => removeAttachment(index)}
                  >
                    <MdCancel className="size-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <textarea
            ref={textareaRef}
            value={body}
            onChange={(e) => {
              setBody(e.target.value);
              resizeInput(e.target);
            }}
            placeholder="メッセージを入力してください"
            className="min-h-[75px] w-full resize-none border-0 bg-transparent p-0 text-body-sm text-main placeholder:text-placeholder focus:outline-none"
          />
        </div>
        {attachmentError && <p className="text-body-xs text-danger">{attachmentError}</p>}
        <div className="flex items-center justify-between">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ATTACHMENT_ACCEPT}
            className="hidden"
            onChange={(e) => handleFilesSelected(e.target.files)}
          />
          <Button
            type="button"
            color="secondary"
            size="md"
            iconOnly
            aria-label="ファイルを添付"
            onClick={() => fileInputRef.current?.click()}
          >
            <MdAttachFile className="size-5" />
          </Button>
          <Button type="submit" color="primary" size="md" disabled={!body.trim() || isPosting}>
            送信
          </Button>
        </div>
      </form>
      {chatComments.length === 0 && !isFetching ? (
        <div className="flex flex-col items-center gap-md py-xxl text-main">
          <p className="text-heading-xs">メッセージがありません</p>
          <p className="text-center text-body-sm">
            タイムラインに投稿すると、チーム全体に共有されます。
          </p>
        </div>
      ) : null}
      <div className="flex flex-col gap-sm">
        {chatComments.map((chatComment) => (
          <CompaniesTimelineItem
            key={chatComment.id}
            chatComment={chatComment}
            canDelete={
              chatComment.source_type === 'user' &&
              !!currentUserId &&
              chatComment.author?.id === currentUserId
            }
            onDelete={() => setDeleteTarget(chatComment)}
          />
        ))}
        {hasNextPage && <div ref={loaderRef} className="h-xxl w-full" />}
      </div>
      <CompaniesTimelineDeleteModal
        isOpen={deleteTarget !== null}
        authorName={deleteTarget?.author?.name ?? ''}
        isPending={isDeleting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}
